import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import type { AxiosError } from "axios";
import useSWR from "swr";
import {
  PrimaryButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../../components/Buttons/Buttons";
import {
  CollapseIcon,
  ExpandIcon,
} from "../../../../../../components/Icons/Icons";
import { Loader } from "../../../../../../components/Loader/Loader";
import { ErrorPlaceholder } from "../../../../../../components/Error";
import { PageTitle } from "../../../../../../layout/portalPageLayout";
import { Breadcrumb } from "../../../../../../components/ProductsLayout/SharedProductsLayout";
import { endpoints } from "../../../../../../endpoints";
import type { PIMProduct } from "../../../../../../types/types.PIM";
import type { ProfileComparisonConfigsResponse } from "../../../../../../types/types";
import type {
  EntityProfile,
  EntityType,
} from "../../../../../../types/types.MDM";
import { useAuthContext } from "../../../../../../components/Auth";
import { useRoutePath } from "../../../../../../util/Routing";
import { makeUrlWithParams, useStoreState } from "../../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import { CompareProductHeader } from "../../../../../SharedPages/OrganizationPage/ProductsList/CompareProducts/CompareProductHeader";
import { CompareGroupSection } from "../../../../../SharedPages/OrganizationPage/ProductsList/CompareProducts/CompareGroupSection";
import { CompareCollectionSection } from "../../../../../SharedPages/OrganizationPage/ProductsList/CompareProducts/CompareCollectionSection";
import { ExportCompareModal } from "../../../../../SharedPages/OrganizationPage/ProductsList/CompareProducts/ExportCompareModal";
import type { CompareConfigItem } from "../../../../../SharedPages/OrganizationPage/ProductsList/CompareProducts/CompareProducts.util";
import {
  getCompareSections,
  MAX_COMPARE_PRODUCTS,
  MIN_COMPARE_PRODUCTS,
} from "../../../../../SharedPages/OrganizationPage/ProductsList/CompareProducts/CompareProducts.util";
import { ENTITY_PERMISSIONS, getEntityDetailEndpoint } from "../utils";
import { entityProfileToCompareProduct } from "./entityToCompareProduct";
import { SELECTED_ENTITY_ROWS_KEYS } from "./compareEntityMeta";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  min-height: 100%;
  background: ${({ theme }) => theme.resolve("color_neutral_white")};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ExpandControls = styled.div`
  display: flex;
  align-items: center;
  margin: ${({ theme }) => theme.resolve("spacing_lg")} 0;

  button {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.resolve("spacing_sm")};
  }
`;

const Sections = styled.div`
  display: flex;
  flex-direction: column;
`;

function parseEntityIds(search: string): string[] {
  const params = new URLSearchParams(search);
  const raw = params.get("entities") || "";
  return [
    ...new Set(
      raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    ),
  ].slice(0, MAX_COMPARE_PRODUCTS);
}

function getListPath(adminPath: string, entityType: EntityType): string {
  return `${adminPath}/mdm/${entityType}s`;
}

async function fetchEntity(
  entityType: EntityType,
  tenantId: string,
  entityId: string,
  returnStaged: boolean
): Promise<EntityProfile> {
  const url = makeUrlWithParams(
    getEntityDetailEndpoint(entityType, tenantId, entityId),
    {
      return_staged: returnStaged ? "true" : "false",
    }
  );
  const { data } = await axios.get<EntityProfile>(url);
  return data;
}

type CompareEntitiesPageProps = {
  entityType: EntityType;
};

export function CompareEntitiesPage({
  entityType,
}: CompareEntitiesPageProps): JSX.Element {
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const { tenant_id } = useStoreState();
  const history = useHistory();
  const location = useLocation();
  const { hasPermission } = useAuthContext();
  const { getLabel } = useMdmEntityDisplayNames();

  const listPath = getListPath(adminPath, entityType);
  const entityLabelPlural = getLabel(entityType, "plural");

  const returnStaged =
    hasPermission(ENTITY_PERMISSIONS[entityType].modify) ||
    hasPermission(ENTITY_PERMISSIONS[entityType].publish);

  const entityIds = useMemo(
    () => parseEntityIds(location.search),
    [location.search]
  );

  const [products, setProducts] = useState<PIMProduct[] | null>(null);
  const [error, setError] = useState<AxiosError | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [allExpanded, setAllExpanded] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);

  const templateId = products?.[0]?.product_schema?.template_id ?? null;
  const { data: comparisonConfigs } = useSWR<ProfileComparisonConfigsResponse>(
    tenant_id && templateId
      ? endpoints.v2_tenants_id_pim_templates_id_settings_profileComparison(
          tenant_id,
          templateId
        )
      : null
  );
  const configsLoaded = !templateId || comparisonConfigs != null;

  const compareConfigItems: CompareConfigItem[] = useMemo(
    () =>
      (comparisonConfigs?.data ?? []).map((row) => ({
        target_type: row.target_type,
        target_id: row.target_id,
        target_name: row.target_name,
        target_display_name: row.target_display_name,
        key_attribute_name: row.key_attribute_name,
      })),
    [comparisonConfigs]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!tenant_id || entityIds.length < MIN_COMPARE_PRODUCTS) {
        setProducts(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results: PIMProduct[] = [];
        for (const id of entityIds) {
          const entity = await fetchEntity(
            entityType,
            tenant_id,
            id,
            returnStaged
          );
          results.push(entityProfileToCompareProduct(entity));
        }
        if (!cancelled) {
          setProducts(results);
        }
      } catch (err) {
        if (!cancelled) {
          setProducts(null);
          setError(err as AxiosError);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [tenant_id, entityIds, returnStaged, entityType]);

  const sections = useMemo(
    () =>
      products?.length
        ? getCompareSections(products[0], products, compareConfigItems)
        : [],
    [products, compareConfigItems]
  );

  useEffect(() => {
    if (!sections.length) return;
    const next: Record<string, boolean> = {};
    for (const section of sections) {
      next[section.id] = true;
    }
    setExpandedMap(next);
    setAllExpanded(true);
  }, [sections]);

  const updateUrlEntities = useCallback(
    (nextIds: string[]) => {
      const params = new URLSearchParams();
      params.set("entities", nextIds.join(","));
      history.replace(`${listPath}/compare?${params.toString()}`);
      sessionStorage.setItem(
        SELECTED_ENTITY_ROWS_KEYS[entityType],
        JSON.stringify(nextIds)
      );
    },
    [history, listPath, entityType]
  );

  const handleRemove = (productNumber: string) => {
    // product_number is entity_number || id from the adapter; resolve back to id.
    const next = entityIds.filter((id) => {
      const product = products?.find((p) => p.id === id);
      const key = product?.product_number || product?.id || id;
      return key !== productNumber && id !== productNumber;
    });
    if (next.length < MIN_COMPARE_PRODUCTS) {
      history.push(listPath);
      return;
    }
    updateUrlEntities(next);
  };

  const toggleSection = (id: string) => {
    setExpandedMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      setAllExpanded(Object.values(next).every(Boolean));
      return next;
    });
  };

  const handleExpandCollapseAll = () => {
    const nextExpanded = !allExpanded;
    const next: Record<string, boolean> = {};
    for (const section of sections) {
      next[section.id] = nextExpanded;
    }
    setExpandedMap(next);
    setAllExpanded(nextExpanded);
  };

  const getEntityHref = (product: PIMProduct) => `${listPath}/${product.id}`;

  const compareTitle = t("Compare {{entityLabel}}", {
    entityLabel: entityLabelPlural,
  });

  if (entityIds.length < MIN_COMPARE_PRODUCTS) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t("Select at least 2 {{entityLabel}} to compare", {
            entityLabel: entityLabelPlural.toLowerCase(),
          })}
          showBackButton
          backUrl={listPath}
        />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t("Could not load {{entityLabel}} for comparison", {
            entityLabel: entityLabelPlural.toLowerCase(),
          })}
          showBackButton
          backUrl={listPath}
        />
      </PageContainer>
    );
  }

  if (loading || !products || !configsLoaded) {
    return (
      <PageContainer>
        <Loader isLoading />
      </PageContainer>
    );
  }

  const hasComparisonConfig =
    (comparisonConfigs?.count ?? comparisonConfigs?.data?.length ?? 0) > 0;
  if (!hasComparisonConfig) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t(
            "No groups or collections are configured for comparison on this template"
          )}
          showBackButton
          backUrl={listPath}
        />
      </PageContainer>
    );
  }

  const templateIds = new Set(
    products.map((p) => p.product_schema.template_id)
  );
  if (templateIds.size > 1) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t(
            "{{entityLabel}} must be of the same template to compare",
            { entityLabel: entityLabelPlural }
          )}
          showBackButton
          backUrl={listPath}
        />
      </PageContainer>
    );
  }

  const uniqueIds = new Set(products.map((p) => p.id));
  if (uniqueIds.size !== products.length) {
    return (
      <PageContainer>
        <ErrorPlaceholder
          message={t("Could not load {{entityLabel}} for comparison", {
            entityLabel: entityLabelPlural.toLowerCase(),
          })}
          showBackButton
          backUrl={listPath}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Breadcrumb>
        <Link to={listPath}>{entityLabelPlural}</Link>
        {" / "}
        <span>{compareTitle}</span>
      </Breadcrumb>
      <TitleRow>
        <PageTitle>{compareTitle}</PageTitle>
        <PrimaryButtonMediumV2 onClick={() => setShowExportModal(true)}>
          {t("Export")}
        </PrimaryButtonMediumV2>
      </TitleRow>

      <CompareProductHeader
        products={products}
        getProductHref={getEntityHref}
        onRemove={handleRemove}
        canRemove
      />

      <ExpandControls>
        <TertiaryButtonMediumV2 onClick={handleExpandCollapseAll}>
          {allExpanded ? (
            <>
              <CollapseIcon /> {t("Collapse All")}
            </>
          ) : (
            <>
              <ExpandIcon /> {t("Expand All")}
            </>
          )}
        </TertiaryButtonMediumV2>
      </ExpandControls>

      <Sections>
        {sections.map((section) =>
          section.kind === "group" ? (
            <CompareGroupSection
              key={section.id}
              groupName={section.name}
              displayName={section.display_name}
              products={products}
              expanded={!!expandedMap[section.id]}
              onToggle={() => toggleSection(section.id)}
            />
          ) : (
            <CompareCollectionSection
              key={section.id}
              collectionName={section.name}
              displayName={section.display_name}
              products={products}
              expanded={!!expandedMap[section.id]}
              onToggle={() => toggleSection(section.id)}
              keyAttributeName={section.keyAttributeName}
            />
          )
        )}
      </Sections>

      <ExportCompareModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        products={products}
        sections={sections}
        entityName={entityType}
      />
    </PageContainer>
  );
}

/** Route entry points — one per entity type so Routes stay declarative. */
export function CompareCustomersPage() {
  return <CompareEntitiesPage entityType="customer" />;
}

export function CompareSuppliersPage() {
  return <CompareEntitiesPage entityType="supplier" />;
}

export function CompareDistributorsPage() {
  return <CompareEntitiesPage entityType="distributor" />;
}
